import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import DashboardLayout from './DashboardLayout';
import { showingService } from '../../_services/showing.service';
import { userService } from '../../_services';

function useCurrentUser() {
  const [user, setUser] = useState(userService.currentUserValue);
  useEffect(() => {
    const sub = userService.currentUser.subscribe(setUser);
    return () => sub.unsubscribe();
  }, []);
  return user;
}

const TILES = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const DATE_WINDOWS = [
  { key: 'all', label: 'Any time', test: () => true },
  { key: 'today', label: 'Today', test: s => new Date(s.window_start).toDateString() === new Date().toDateString() },
  { key: 'tomorrow', label: 'Tomorrow', test: s => new Date(s.window_start).toDateString() === new Date(Date.now() + 864e5).toDateString() },
  { key: 'week', label: 'Next 7 days', test: s => new Date(s.window_start) <= new Date(Date.now() + 7 * 864e5) }
];

function fmtWindow(s) {
  const a = new Date(s.window_start), b = new Date(s.window_end);
  const day = a.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const tm = d => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${tm(a)}–${tm(b)}`;
}

function coordsOf(job) {
  const p = job.properties && job.properties[0];
  if (p && p.location && p.location.coordinates && p.location.coordinates.length === 2) {
    return [p.location.coordinates[1], p.location.coordinates[0]]; // [lat, lng]
  }
  return null;
}

function pinIcon(fee, selected) {
  return L.divIcon({
    className: 'mp-pin-wrap',
    html: `<div class="mp-pin${selected ? ' selected' : ''}"><span>$${fee}</span></div>`,
    iconSize: [58, 40],
    iconAnchor: [29, 40]
  });
}

export default function ShowingsMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const meLayerRef = useRef(null);
  const fittedRef = useRef(false);

  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [feeMin, setFeeMin] = useState('');
  const [dateWin, setDateWin] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const user = useCurrentUser();

  // ── map init
  useEffect(() => {
    const map = L.map(mapEl.current, {
      center: [37.5, -96.5],
      zoom: 4,
      zoomControl: false,
      attributionControl: true
    });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer(TILES, { attribution: ATTRIB, subdomains: 'abcd', maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // ── data
  const load = useCallback(() => {
    showingService.jobs().then(res => {
      if (res.status === 'success') { setJobs(res.showings); setLoadError(null); }
      else setLoadError(res.message || 'Could not load showings.');
      setLoaded(true);
    }).catch(() => { setLoadError('Could not load showings.'); setLoaded(true); });
  }, []);

  useEffect(() => {
    load();
    const tmr = setInterval(load, 45000);
    return () => clearInterval(tmr);
  }, [load]);

  // ── filtering
  const dw = DATE_WINDOWS.find(d => d.key === dateWin) || DATE_WINDOWS[0];
  const filtered = jobs.filter(j => {
    if (feeMin && j.fee < parseFloat(feeMin)) return false;
    if (!dw.test(j)) return false;
    if (q) {
      const hay = ((j.properties[0] || {}).city + ' ' + (j.properties[0] || {}).state + ' ' + (j.properties[0] || {}).zip + ' ' + (j.notes || '')).toLowerCase();
      if (hay.indexOf(q.toLowerCase()) === -1) return false;
    }
    return true;
  });

  // ── markers sync
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const keep = {};
    filtered.forEach(j => {
      const ll = coordsOf(j);
      if (!ll) return;
      keep[j.request_id] = true;
      let m = markersRef.current[j.request_id];
      const isSel = selected && selected.request_id === j.request_id;
      if (!m) {
        m = L.marker(ll, { icon: pinIcon(j.fee, isSel), riseOnHover: true });
        m.on('click', () => setSelected(j));
        m.addTo(map);
        markersRef.current[j.request_id] = m;
      } else {
        m.setIcon(pinIcon(j.fee, isSel));
      }
    });
    Object.keys(markersRef.current).forEach(id => {
      if (!keep[id]) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });

    // fit once when data first arrives
    if (!fittedRef.current && filtered.length) {
      const pts = filtered.map(coordsOf).filter(Boolean);
      if (pts.length) {
        map.fitBounds(L.latLngBounds(pts).pad(0.25), { maxZoom: 11 });
        fittedRef.current = true;
      }
    }
  }, [filtered, selected]);

  // ── actions
  const focusJob = j => {
    setSelected(j);
    const ll = coordsOf(j);
    if (ll && mapRef.current) mapRef.current.flyTo(ll, Math.max(mapRef.current.getZoom(), 11), { duration: 0.8 });
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(pos => {
      const map = mapRef.current;
      if (!map) return;
      const ll = [pos.coords.latitude, pos.coords.longitude];
      if (meLayerRef.current) map.removeLayer(meLayerRef.current);
      meLayerRef.current = L.layerGroup([
        L.circle(ll, { radius: 40233, color: '#17503a', weight: 1.5, fillColor: '#17503a', fillOpacity: 0.06, dashArray: '6 6' }),
        L.circleMarker(ll, { radius: 7, color: '#fff', weight: 2.5, fillColor: '#17503a', fillOpacity: 1 })
      ]).addTo(map);
      map.flyTo(ll, 10, { duration: 1 });
    });
  };

  const accept = j => {
    setBusy(true);
    setNotice(null);
    showingService.accept(j.request_id).then(res => {
      setBusy(false);
      if (res.status === 'success') {
        setNotice({ kind: 'ok', text: 'Accepted! Find it under Showing Appointments — access notes are now unlocked.' });
        setSelected(null);
        load();
      } else if (res.status === 'license_not_verified') {
        setNotice({ kind: 'err', text: <>Your license must be verified before accepting. <Link to="/users/profile/">Submit it from your profile</Link>.</> });
      } else if (res.status === 'profile_incomplete') {
        setNotice({ kind: 'err', text: <>Complete your profile before accepting. <Link to="/complete-profile">Go to your profile</Link>.</> });
      } else if (res.status === 'already_taken') {
        setNotice({ kind: 'err', text: 'Someone beat you to that one.' });
        setSelected(null);
        load();
      } else {
        setNotice({ kind: 'err', text: res.message || 'Could not accept.' });
      }
    }).catch(() => { setBusy(false); setNotice({ kind: 'err', text: 'Network error — try again.' }); });
  };

  const isMine = j => user && j.buyer_agent && String(j.buyer_agent.user_id) === String(user._id);

  return (
    <DashboardLayout>
      <div className="mp-head">
        <div>
          <h1 style={{ margin: 0 }}>Showings map</h1>
          <p className="sh-page-sub" style={{ margin: '4px 0 0' }}>{filtered.length} open showing{filtered.length === 1 ? '' : 's'} on the board</p>
        </div>
        <Link className="sh-btn sh-btn-outline" to="/jobs">List view</Link>
      </div>

      <div className="mp-wrap">
        <div className="mp-map" ref={mapEl} />

        {/* filter toolbar */}
        <div className="mp-toolbar">
          <div className="mp-seg">
            {DATE_WINDOWS.map(d => (
              <button key={d.key} className={'mp-seg-btn' + (dateWin === d.key ? ' on' : '')} onClick={() => setDateWin(d.key)}>{d.label}</button>
            ))}
          </div>
          <div className="mp-fee">
            <span>$</span>
            <input type="number" min="0" placeholder="Min fee" value={feeMin} onChange={e => setFeeMin(e.target.value)} />
          </div>
          <input className="mp-search" placeholder="City, state, or ZIP…" value={q} onChange={e => setQ(e.target.value)} />
          <button className="mp-locate" onClick={locateMe} title="Show my area">◎ Near me</button>
        </div>

        {loadError ? <div className="sh-error" style={{ margin: '0 0 12px' }}>{loadError}</div> : null}

        {/* results rail */}
        <div className="mp-rail">
          {!loaded ? (
            <div className="mp-rail-empty">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="mp-rail-empty">No open showings match these filters.</div>
          ) : filtered.map(j => {
            const p = j.properties[0] || {};
            const sel = selected && selected.request_id === j.request_id;
            return (
              <button key={j.request_id} className={'mp-card' + (sel ? ' on' : '')} onClick={() => focusJob(j)}>
                <div className="mp-card-top">
                  <b>{[p.city, p.state].filter(Boolean).join(', ') || 'Showing'}</b>
                  <span className="mp-card-fee">${j.fee}</span>
                </div>
                <div className="mp-card-meta">{fmtWindow(j)} · {j.duration_minutes} min{j.properties.length > 1 ? ` · ${j.properties.length} stops` : ''}</div>
              </button>
            );
          })}
        </div>

        {/* selected detail card */}
        {selected ? (
          <div className="mp-detail">
            <button className="mp-detail-close" onClick={() => setSelected(null)}>&times;</button>
            <div className="mp-detail-fee">${selected.fee}</div>
            <b>{[(selected.properties[0] || {}).city, (selected.properties[0] || {}).state, (selected.properties[0] || {}).zip].filter(Boolean).join(', ')}</b>
            <div className="mp-card-meta" style={{ margin: '6px 0' }}>
              {fmtWindow(selected)} · {selected.duration_minutes} min
              {selected.properties.length > 1 ? ` · ${selected.properties.length} stops` : ''}
            </div>
            {selected.notes ? <p className="mp-detail-notes">{selected.notes}</p> : null}
            <div className="mp-card-meta" style={{ marginBottom: 10 }}>
              Posted by {selected.buyer_agent && selected.buyer_agent.name}
            </div>
            {isMine(selected) ? (
              <span className="sh-status open">Your posting</span>
            ) : (
              <button className="sh-btn sh-btn-green" style={{ width: '100%' }} disabled={busy} onClick={() => accept(selected)}>
                {busy ? 'Accepting…' : 'Accept this showing'}
              </button>
            )}
          </div>
        ) : null}

        {notice ? (
          <div className={'mp-notice ' + notice.kind} onClick={() => setNotice(null)}>{notice.text}</div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
